import "./UpgradeModal.scss";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  findEventTierFor,
  isCapabilityReason,
  QUOTA_DIMENSION,
} from "@/config/plans";
import { Button, Modal } from "@/covaltech-react-ui";
import useCheckout from "@/hooks/useCheckout";
import useLocalizedPath from "@/i18n/useLocalizedPath";
import { useUpgradeStore } from "@/stores/UpgradeStore";

/**
 * Explains a refused action instead of leaving the user with a red toast.
 *
 * Mounted once at the app root and driven by `UpgradeStore`, which `appFetch`
 * fills on any 402. The breach payload carries the dimension, the current usage
 * and the limit, so the message names the exact wall that was hit, and, for a
 * volunteer cap, the smallest package that would clear it.
 *
 * When that package can be bought for a known event, the primary button starts
 * the payment right there: sending someone to the pricing page to find the tier
 * we just named would be busywork.
 */
const UpgradeModal = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { quota, openCount, closeUpgrade } = useUpgradeStore();
  const { startEventTier, isPending } = useCheckout();
  const localizedPath = useLocalizedPath();

  if (!quota) {
    return null;
  }

  const pricingPath = localizedPath("pricing");

  // Two kinds of refusal: a counter that filled up, or a feature the plan does
  // not carry at all. The second has no "x of y" to show and no event package
  // to suggest, only a plan does.
  const isCapability = isCapabilityReason(quota);
  const isVolunteerCap =
    !isCapability && quota.dimension === QUOTA_DIMENSION.VOLUNTEERS_PER_EVENT;
  // `required` is the coverage the backend says clears the block, and it is not
  // always `current + 1`: adding a volunteer is projected, while "this event
  // must fit what it already holds" is not. Guessing with a blanket `+1` sold
  // the next package up at every boundary: 99 € for a 50-person event that the
  // 49 € one covers exactly. The fallback keeps the old behaviour for a backend
  // that does not send the field yet.
  const suggestedTier =
    isVolunteerCap && !isCapability
      ? findEventTierFor(quota.required ?? quota.current + 1)
      : null;
  const eventId = isCapability ? undefined : quota.event_id;
  const canBuyNow = Boolean(suggestedTier && eventId);

  const goToPricing = () => {
    closeUpgrade();
    navigate(pricingPath);
  };

  return (
    <Modal
      // Re-mounts on every breach so a prompt dismissed with the close icon
      // still reopens the next time an action is refused.
      key={openCount}
      defaultOpen
      className="upgradeModal"
      modalCloseIcon
      cancelLabel={t("upgrade.later")}
      modalButton={() => (
        <span className="upgradeModalAnchor" aria-hidden="true" />
      )}
      modalTitle={t("upgrade.title")}
      modalContent={
        <div className="upgradeContent">
          <p className="upgradeReason">
            {isCapability
              ? t(`upgrade.feature.${quota.capability}`)
              : t(`upgrade.reason.${quota.dimension}`, {
                  current: quota.current,
                  limit: quota.limit,
                })}
          </p>

          {suggestedTier && (
            <p className="upgradeSuggestion">
              {t("upgrade.suggested_tier", {
                tier: t(`pricing.event_tiers.tiers.${suggestedTier.id}.name`),
                volunteers: suggestedTier.volunteers,
                price: suggestedTier.price,
              })}
            </p>
          )}

          {isVolunteerCap && !suggestedTier && (
            <p className="upgradeSuggestion">{t("upgrade.contact_us")}</p>
          )}

          <p className="upgradeHint">{t("upgrade.hint")}</p>

          {canBuyNow && (
            <button
              type="button"
              className="upgradeSecondary"
              onClick={goToPricing}
            >
              {t("upgrade.see_plans")}
            </button>
          )}
        </div>
      }
      modalFooterButton={({ onClose }) =>
        canBuyNow && suggestedTier && eventId ? (
          <Button
            label={t("upgrade.unlock_now", { price: suggestedTier.price })}
            style="blue"
            isLoading={isPending}
            onClick={() => {
              // The modal stays open until Stripe takes over: closing it first
              // would flash the blocked screen behind an imminent redirect.
              void startEventTier(eventId, suggestedTier.id);
            }}
          />
        ) : (
          <Button
            label={t("upgrade.see_plans")}
            style="blue"
            onClick={() => {
              onClose();
              goToPricing();
            }}
          />
        )
      }
    />
  );
};

export default UpgradeModal;
